import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dayjs } from 'dayjs';
import { Tooltip, styled } from '@mui/material';

import { CustomButton, CustomInput, Picker } from '../../../shared/uiComponents';
import { findDuration } from '../../Calendar/Helpers/functions';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import { InfoFilled } from '../../../shared/Assets/svgIcons';
import { DEFAULT_TIMEZONE_ID, getWallClockTimeZoneAbbreviation, parseWallClockTime } from '../../../shared/Helpers/timezones';

const TimeWrapper = styled('div')(() => ({
	display: 'flex',
	gap: '32px',
	width: '100%',
	flexWrap: 'wrap',
	alignItems: 'center',
}));

const TimeInfo = ({ disabled, save }: { disabled?: boolean; save?: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const { date, startTime, endTime, client } = useSelector((state) => state.session.sessionInfo);
	const noteDate = date ? date.split('T')[0] : '';
	const tzSuffix =
		client?.timeZoneId && client.timeZoneId !== DEFAULT_TIMEZONE_ID
			? ` (${getWallClockTimeZoneAbbreviation(client.timeZoneId, startTime)})`
			: '';

	const [hasTimeChanged, setHasTimeChanged] = useState<boolean>(false);

	useEffect(() => {
		if (!hasTimeChanged || !save) return;
		save();
		setHasTimeChanged(false);
	}, [hasTimeChanged, save]);

	const startTimeHandler = (value: Dayjs | null) => {
		if (!value) return;
		// The picker works in wall-clock mode (naive datetimes). The provider sees
		// the stored wall-clock (already in the client's timezone) and edits it
		// directly — no instant-to-timezone conversion needed here.
		const startTimeValue = value.format().slice(10, 19);
		dispatch(setStartTime(`${noteDate}${startTimeValue}`));
		setHasTimeChanged(true);
	};

	const endTimeHandler = (value: Dayjs | null) => {
		if (!value) return;
		const endTimeValue = value.format().slice(10, 19);
		dispatch(setEndTime(`${noteDate}${endTimeValue}`));
		setHasTimeChanged(true);
	};

	return startTime && endTime ? (
		<TimeWrapper className="marginBottom16">
			<Picker.CustomTime label={`Start time${tzSuffix}`} onChange={startTimeHandler} value={parseWallClockTime(startTime)} disabled={disabled} />
			<Picker.CustomTime label={`End time${tzSuffix}`} onChange={endTimeHandler} value={parseWallClockTime(endTime)} disabled={disabled} />
			<Tooltip title="If different, the respective session time will be updated once the note is submitted.">
				<span style={{ marginTop: '24px' }}>
					<CustomButton iconOnly={<InfoFilled />} onClick={() => null} buttonType="secondaryGray" disabled />
				</span>
			</Tooltip>
			<CustomInput label="Duration:" value={findDuration(startTime, endTime)} disabled />
		</TimeWrapper>
	) : null;
};

export default TimeInfo;
